import React, { useState, useEffect } from 'react';
import { AlertTriangle, FileText, Search, Plus, Filter, Download, FileDown, ClipboardList } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getCurrentUser } from '@/data/users';

// School-specific discipline documents
const schoolDocuments = {
  'school-stpatricks-college': [
    {
      id: 'doc-1',
      name: 'Discipline Procedure',
      description: 'Official discipline procedure guidelines for St Patrick\'s College',
      url: 'https://customer-assets.emergentagent.com/job_fef6b712-b8fc-4b72-95db-2ec6aee6123b/artifacts/9jmf0c94_Discipline%20Proceedure.docx',
      type: 'docx',
      icon: 'procedure'
    },
    {
      id: 'doc-2',
      name: 'Counselling Form',
      description: 'Student counselling documentation form',
      url: 'https://customer-assets.emergentagent.com/job_fef6b712-b8fc-4b72-95db-2ec6aee6123b/artifacts/qsfz4tfy_Counselling%20Form.docx',
      type: 'docx',
      icon: 'form'
    }
  ]
};

export default function DisciplinePage() {
  const currentUser = getCurrentUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [disciplineRecords, setDisciplineRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get documents for current user's school
  const documents = schoolDocuments[currentUser?.school_id] || [];
  
  useEffect(() => {
    setTimeout(() => {
      setDisciplineRecords([
        { id: '1', student_name: 'Sample Student A', student_class: 'Year 9', incident_type: 'Late Arrival', severity: 'minor', description: 'Arrived 30 minutes late', date: '2025-03-10', status: 'resolved', action_taken: 'Verbal warning' },
        { id: '2', student_name: 'Sample Student B', student_class: 'Year 10', incident_type: 'Uniform Violation', severity: 'minor', description: 'Not wearing proper shoes', date: '2025-03-09', status: 'pending', action_taken: null },
        { id: '3', student_name: 'Sample Student C', student_class: 'Year 8', incident_type: 'Disruptive Behavior', severity: 'moderate', description: 'Disrupting class', date: '2025-03-08', status: 'resolved', action_taken: 'Parent meeting' },
        { id: '4', student_name: 'Sample Student D', student_class: 'Year 9', incident_type: 'Truancy', severity: 'major', description: 'Absent 2 days without permission', date: '2025-03-07', status: 'in_progress', action_taken: 'Under investigation' }
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getSeverityBadge = (severity) => {
    const styles = { minor: 'bg-yellow-100 text-yellow-800', moderate: 'bg-orange-100 text-orange-800', major: 'bg-red-100 text-red-800' };
    return <Badge className={styles[severity] || 'bg-gray-100'}>{severity}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = { pending: 'bg-gray-100 text-gray-800', in_progress: 'bg-blue-100 text-blue-800', resolved: 'bg-green-100 text-green-800' };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status.replace('_', ' ')}</Badge>;
  };

  const filteredRecords = disciplineRecords.filter(record => {
    const matchesSearch = record.student_name.toLowerCase().includes(searchTerm.toLowerCase()) || record.incident_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = selectedSeverity === 'all' || record.severity === selectedSeverity;
    return matchesSearch && matchesSeverity;
  });

  const stats = {
    total: disciplineRecords.length,
    pending: disciplineRecords.filter(r => r.status === 'pending').length,
    resolved: disciplineRecords.filter(r => r.status === 'resolved').length,
    major: disciplineRecords.filter(r => r.severity === 'major').length
  };

  return (
    <div className="space-y-6" data-testid="discipline-page">
      <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-red-500" />
            Discipline Management
          </h1>
          <p className="text-muted-foreground mt-1">Monitor and manage student discipline records</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-incident-btn"><Plus className="h-4 w-4 mr-2" />Record Incident</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Record New Discipline Incident</DialogTitle>
              <DialogDescription>Document a student discipline incident for tracking.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2"><Label>Student Name</Label><Input placeholder="Enter student name" /></div>
              <div className="grid gap-2"><Label>Class</Label>
                <Select><SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>
                    {[7,8,9,10,11,12,13].map(y => <SelectItem key={y} value={`year${y}`}>Year {y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2"><Label>Incident Type</Label>
                <Select><SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="late">Late Arrival</SelectItem>
                    <SelectItem value="uniform">Uniform Violation</SelectItem>
                    <SelectItem value="behavior">Disruptive Behavior</SelectItem>
                    <SelectItem value="truancy">Truancy</SelectItem>
                    <SelectItem value="bullying">Bullying</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2"><Label>Severity</Label>
                <Select><SelectTrigger><SelectValue placeholder="Select severity" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2"><Label>Description</Label><Textarea placeholder="Describe the incident..." rows={3} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
              <Button onClick={() => setIsAddDialogOpen(false)}>Save Record</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Total Records</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{stats.total}</div></CardContent></Card>
        <Card className="border-l-4 border-l-yellow-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Pending Review</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-yellow-600">{stats.pending}</div></CardContent></Card>
        <Card className="border-l-4 border-l-green-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Resolved</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-green-600">{stats.resolved}</div></CardContent></Card>
        <Card className="border-l-4 border-l-red-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Major Incidents</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-red-600">{stats.major}</div></CardContent></Card>
      </div>

      {/* School Documents Section - Only shown if documents exist for this school */}
      {documents.length > 0 && (
        <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-purple-600" />
              Discipline Documents & Forms
            </CardTitle>
            <CardDescription>Official discipline procedure documents and forms for your school</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {documents.map((doc) => (
                <div key={doc.id} className="bg-white rounded-lg p-4 border border-purple-200 hover:border-purple-400 transition-colors shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${doc.icon === 'procedure' ? 'bg-blue-100' : 'bg-green-100'}`}>
                        {doc.icon === 'procedure' ? (
                          <FileText className="h-6 w-6 text-blue-600" />
                        ) : (
                          <ClipboardList className="h-6 w-6 text-green-600" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{doc.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>
                        <Badge variant="outline" className="mt-2 text-xs">.{doc.type.toUpperCase()}</Badge>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="shrink-0"
                      onClick={() => window.open(doc.url, '_blank')}
                      data-testid={`download-${doc.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by student name or incident type..." className="pl-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="search-input" />
            </div>
            <Select value={selectedSeverity} onValueChange={setSelectedSeverity}>
              <SelectTrigger className="w-[180px]" data-testid="severity-filter"><Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Filter" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="minor">Minor</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="major">Major</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Discipline Records</CardTitle><CardDescription>Recent incidents and their status</CardDescription></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
          ) : filteredRecords.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground"><AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No discipline records found</p></div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Class</TableHead><TableHead>Incident Type</TableHead><TableHead>Severity</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredRecords.map((record) => (
                  <TableRow key={record.id} data-testid={`record-${record.id}`}>
                    <TableCell className="font-medium">{record.student_name}</TableCell>
                    <TableCell>{record.student_class}</TableCell>
                    <TableCell>{record.incident_type}</TableCell>
                    <TableCell>{getSeverityBadge(record.severity)}</TableCell>
                    <TableCell>{record.date}</TableCell>
                    <TableCell>{getStatusBadge(record.status)}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="sm"><FileText className="h-4 w-4 mr-1" />View</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
